import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../services/api.client';
import { authService } from '../services/auth.service';

/**
 * Per-company module/feature toggles from Platform → Module & Feature Settings.
 *
 * A feature key (e.g. "employee", "attendance.mark") is enabled when it is in the set the server
 * resolves for the current company (defaults overlaid with the company's overrides; disabling a
 * module cascades to its submodules at save time). The Sidebar and the Dashboard route guard read
 * this so a disabled module disappears from navigation and cannot be opened by URL.
 *
 * Fail-OPEN while loading, unlike permissions: features default to enabled, and hiding the entire
 * app during a slow fetch is worse than a disabled item briefly showing. The route guard defers
 * until the set has loaded, so nothing disabled is actually reachable.
 */
interface FeatureContextValue {
  /** Null until a fetch succeeds. Null ⇒ unknown ⇒ everything visible (fail-open). */
  enabledFeatures: Set<string> | null;
  loaded: boolean;
  /** True when the feature is enabled, or not gated at all (no key / set unknown). */
  isFeatureEnabled: (key?: string) => boolean;
  refresh: () => Promise<void>;
}

const FeatureContext = createContext<FeatureContextValue>({
  enabledFeatures: null,
  loaded: false,
  isFeatureEnabled: () => true,
  refresh: async () => {},
});

/** Broadcast from Module & Feature Settings after a save, so the portal reflects it at once. */
export const FEATURES_CHANGED_EVENT = 'features-changed';

export const FeatureProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [enabledFeatures, setEnabledFeatures] = useState<Set<string> | null>(null);
  const [loaded, setLoaded] = useState(false);
  const lastFocusFetchRef = useRef(0);

  const fetchFeatures = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setEnabledFeatures(null);
      setLoaded(true);
      return;
    }
    try {
      const res = await apiClient.get<{ enabledFeatures: string[] }>('/features/my');
      setEnabledFeatures(new Set((res.data.enabledFeatures ?? []).map(k => k.toLowerCase())));
    } catch {
      // Leave the app fully visible rather than blank — the toggle is an availability
      // convenience, not a security boundary (permissions are that). null ⇒ nothing gated.
      setEnabledFeatures(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchFeatures(); }, [fetchFeatures]);

  // Re-fetch after login, and after a Company switch — module toggles are per-company.
  useEffect(() => {
    const onAuthChanged = () => { setLoaded(false); fetchFeatures(); };
    window.addEventListener('auth-changed', onAuthChanged);
    window.addEventListener('company-switched', onAuthChanged);
    return () => {
      window.removeEventListener('auth-changed', onAuthChanged);
      window.removeEventListener('company-switched', onAuthChanged);
    };
  }, [fetchFeatures]);

  // Immediate effect: when an owner saves Module Settings, refresh without a reload.
  useEffect(() => {
    const onChanged = () => fetchFeatures();
    window.addEventListener(FEATURES_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(FEATURES_CHANGED_EVENT, onChanged);
  }, [fetchFeatures]);

  // Pick up an admin's change on window focus (30s cooldown), same as permissions.
  useEffect(() => {
    const onFocus = () => {
      if (!authService.isAuthenticated()) return;
      const now = Date.now();
      if (now - lastFocusFetchRef.current < 30_000) return;
      lastFocusFetchRef.current = now;
      fetchFeatures();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchFeatures]);

  const value: FeatureContextValue = {
    enabledFeatures,
    loaded,
    // No key → not gated. Set unknown (loading or fetch failed) → permissive, so a hiccup never
    // blanks the sidebar. Only an explicit "not in the enabled set" hides a module.
    isFeatureEnabled: (key?: string) => !key || enabledFeatures === null || enabledFeatures.has(key.toLowerCase()),
    refresh: fetchFeatures,
  };

  return <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>;
};

export const useFeatures = () => useContext(FeatureContext);
